import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { InfoCircle, Eye, Box, ScanBarcode, Profile2User } from "iconsax-react";
import { QRCodeCanvas } from "qrcode.react";
import { Table, Dropdown, Button, ModalWrapper, Toast, Badge, DropdownItem } from "@/shared/ui";
import { PERMISSIONS } from "@/shared/consts";
import { useAuth } from "@/shared/hooks";
import { formatMoneyKzt } from "@/features/accounting/shared";
import { toNumber } from "@/shared/utils";
import { MoreIcon } from "@/shared/assets/icons";
import type { InventoryUnit, WarehouseTableRow, AggregatedInventoryRow } from "../types";
import type { SortKey, SortConfig } from "../hooks";
import { getWarehouseStatusConfig, getInventorySubtypeConfig, formatWarehouseDate } from "../utils";
import { INVENTORY_SUBTYPE_LABELS } from "../consts";
import WarehouseActions from "./WarehouseActions";

interface Props {
  rows: WarehouseTableRow[];
  onOpen: (id: number) => void;
  onAssign: (id: number) => void;
  onReturn: (id: number) => void;
  onWriteOff: (id: number) => void;
  onTransfer?: (item: InventoryUnit) => void;
  onRevalue?: (item: InventoryUnit) => void;
  onBatchDetails?: (row: AggregatedInventoryRow) => void;
  onBatchAssign?: (row: AggregatedInventoryRow) => void;
  onBatchWriteOff?: (row: AggregatedInventoryRow) => void;
  sortConfig: SortConfig | null;
  onSort: (key: SortKey) => void;
  assigningId?: number | null;
  writingOffId?: number | null;
  batchAssigningId?: number | null;
  batchWritingOffId?: number | null;
  hideStatusColumn?: boolean;
}

export default function WarehouseTable({
  rows,
  onOpen,
  onAssign,
  onReturn,
  onWriteOff,
  onTransfer,
  onRevalue,
  onBatchDetails,
  onBatchAssign,
  onBatchWriteOff,
  sortConfig,
  onSort,
  assigningId,
  writingOffId,
  batchAssigningId,
  batchWritingOffId,
  hideStatusColumn = false,
}: Props) {
  const { t, i18n } = useTranslation("WarehousePage");
  const _locale = (i18n.language as "ru" | "en" | "kk") || "ru";
  const { hasPermission } = useAuth();
  const canManageInventory = hasPermission(PERMISSIONS.WAREHOUSE.EDIT_INVENTORY);
  const [openDropdownId, setOpenDropdownId] = useState<number | string | null>(null);
  const [hoveredDepreciationId, setHoveredDepreciationId] = useState<number | null>(null);
  const [hoveredSubtypeId, setHoveredSubtypeId] = useState<number | string | null>(null);
  const [qrModalBarcode, setQrModalBarcode] = useState<string | null>(null);
  const qrRef = useRef<HTMLCanvasElement>(null);

  const handleDownloadQR = useCallback(() => {
    const canvas = qrRef.current;
    if (!canvas || !qrModalBarcode) return;

    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.download = `warehouse-unit-${qrModalBarcode}.png`;
    link.href = url;
    link.click();
  }, [qrModalBarcode]);

  const qrUrl = qrModalBarcode ? `${window.location.origin}/warehouse/units/${qrModalBarcode}` : "";

  function renderIndividualRow(unit: InventoryUnit, index: number) {
    const assetTypeLabel = t(`assetType.${unit.asset_type}`) || unit.asset_type;
    const statusLabel = t(`status.${unit.status}`) || unit.status_display;
    const statusConfig = getWarehouseStatusConfig(unit.status);
    const subtypeLabel = unit.inventory_subtype ? INVENTORY_SUBTYPE_LABELS[unit.inventory_subtype]?.[_locale] : null;
    const subtypeConfig = unit.inventory_subtype ? getInventorySubtypeConfig(unit.inventory_subtype) : null;

    return (
      <Table.Row key={unit.id} onClick={() => onOpen(unit.id)} className="cursor-pointer">
        <Table.Cell isBold>
          <div className="flex flex-col gap-1">
            <span>{unit.item_name}</span>
            {unit.assigned_to_name && (
              <span className="text-label-xs content-action-neutral">
                {t("table.assignedTo")}: {unit.assigned_to_name}
              </span>
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div className="flex items-center gap-1">
            <Badge variant="soft" color="gray" text={assetTypeLabel} />
            {subtypeLabel && subtypeConfig && (
              <div
                className="relative flex items-center"
                onMouseEnter={() => setHoveredSubtypeId(unit.id)}
                onMouseLeave={() => setHoveredSubtypeId(null)}>
                <Dropdown
                  open={hoveredSubtypeId === unit.id}
                  onClose={() => setHoveredSubtypeId(null)}
                  direction="bottom"
                  align="left"
                  width="w-max"
                  className="elevation-level-2!">
                  <span
                    className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full cursor-default",
                      subtypeConfig.color === "notice" && "background-notice-subtle content-action-notice",
                      subtypeConfig.color === "info" && "background-info-subtle content-action-info",
                    )}>
                    {subtypeConfig.icon}
                  </span>
                  <div
                    className="px-2.5 py-1.5 text-label-xs"
                    onMouseEnter={() => setHoveredSubtypeId(unit.id)}
                    onMouseLeave={() => setHoveredSubtypeId(null)}>
                    {subtypeLabel}
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
        </Table.Cell>
        {!hideStatusColumn && (
          <Table.Cell>
            <Badge variant="soft" color={statusConfig.color} text={statusLabel} icon={statusConfig.icon} />
          </Table.Cell>
        )}
        <Table.Cell>
          <span className="text-label-sm content-base-primary">{unit.barcode}</span>
        </Table.Cell>
        <Table.Cell>
          <span className="text-label-sm content-base-primary">{unit.serial_number}</span>
        </Table.Cell>
        <Table.Cell>
          <div className="flex flex-col gap-1">
            <span className="text-label-sm content-base-primary">{formatMoneyKzt(unit.unit_cost)}</span>
            {unit.is_depreciable && unit.residual_value && (
              <div className="flex items-center gap-1">
                <span className="text-label-xs content-action-neutral">{t("table.residualValue")}:</span>
                <span className="text-label-xs content-base-primary">
                  {formatMoneyKzt(unit.residual_value)}
                </span>
                {unit.accumulated_depreciation && toNumber(unit.accumulated_depreciation) > 0 && (
                  <div
                    className="relative flex items-center"
                    onMouseEnter={() => setHoveredDepreciationId(unit.id)}
                    onMouseLeave={() => setHoveredDepreciationId(null)}>
                    <Dropdown
                      open={hoveredDepreciationId === unit.id}
                      onClose={() => setHoveredDepreciationId(null)}
                      direction="bottom"
                      align="right"
                      width="w-max"
                      className="elevation-level-2!">
                      <InfoCircle
                        size={14}
                        color="currentColor"
                        className="content-action-neutral cursor-pointer hover:opacity-80"
                      />
                      <div
                        className="p-3 flex flex-col gap-1 min-w-48"
                        onMouseEnter={() => setHoveredDepreciationId(unit.id)}
                        onMouseLeave={() => setHoveredDepreciationId(null)}>
                        <div className="flex justify-between gap-4 text-label-xs">
                          <span className="content-action-neutral">{t("tooltip.originalCost")}:</span>
                          <span className="content-base-primary">{formatMoneyKzt(unit.unit_cost)}</span>
                        </div>
                        <div className="flex justify-between gap-4 text-label-xs">
                          <span className="content-action-neutral">{t("tooltip.accumulated")}:</span>
                          <span className="content-action-negative">
                            {formatMoneyKzt(unit.accumulated_depreciation)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-4 text-label-xs">
                          <span className="content-action-neutral">{t("tooltip.residualValue")}:</span>
                          <span className="content-action-positive">{formatMoneyKzt(unit.residual_value)}</span>
                        </div>
                        {unit.next_depreciation_date && (
                          <div className="flex justify-between gap-4 text-label-xs pt-1 border-t surface-base-stroke">
                            <span className="content-action-neutral">{t("tooltip.nextDepreciation")}:</span>
                            <span className="content-base-primary">
                              {formatWarehouseDate(unit.next_depreciation_date)}
                            </span>
                          </div>
                        )}
                      </div>
                    </Dropdown>
                  </div>
                )}
              </div>
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              isIconButton
              onClick={(e) => {
                e.stopPropagation();
                setQrModalBarcode(unit.barcode);
              }}
              className="w-8! radius-xs! p-0!">
              <ScanBarcode size={16} color="currentColor" />
            </Button>
            <WarehouseActions
              item={unit}
              onAssign={onAssign}
              onReturn={onReturn}
              onWriteOff={onWriteOff}
              onTransfer={onTransfer}
              onRevalue={onRevalue}
              assigningId={assigningId}
              writingOffId={writingOffId}
              isOpen={openDropdownId === unit.id}
              onToggle={(isOpen) => {
                setOpenDropdownId(isOpen ? unit.id : null);
              }}
              direction={index === rows.length - 1 ? "top" : "bottom"}
            />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }

  function renderAggregatedRow(row: AggregatedInventoryRow, index: number) {
    const assetTypeLabel = t(`assetType.${row.assetType}`) || row.assetType;
    const subtypeLabel = row.inventorySubtype ? INVENTORY_SUBTYPE_LABELS[row.inventorySubtype]?.[_locale] : null;
    const subtypeConfig = row.inventorySubtype ? getInventorySubtypeConfig(row.inventorySubtype) : null;
    const dropdownKey = `batch-${row.inventoryBatchId}`;
    const isBatchAssigning = batchAssigningId === row.inventoryBatchId;
    const isBatchWritingOff = batchWritingOffId === row.inventoryBatchId;
    const isBatchLoading = isBatchAssigning || isBatchWritingOff;
    const hasInStock = row.inStockCount > 0;
    const hasWriteOffable = row.inStockCount > 0 || row.assignedCount > 0;

    return (
      <Table.Row key={`batch-${row.inventoryBatchId}`}>
        <Table.Cell isBold>
          <div className="flex flex-col gap-1">
            <span>{row.itemName}</span>
            <span className="text-label-xs content-action-neutral">
              {t("table.quantity")}: {row.totalCount}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell>
          <div className="flex items-center gap-1">
            <Badge variant="soft" color="gray" text={assetTypeLabel} />
            {subtypeLabel && subtypeConfig && (
              <div
                className="relative flex items-center"
                onMouseEnter={() => setHoveredSubtypeId(dropdownKey)}
                onMouseLeave={() => setHoveredSubtypeId(null)}>
                <Dropdown
                  open={hoveredSubtypeId === dropdownKey}
                  onClose={() => setHoveredSubtypeId(null)}
                  direction="bottom"
                  align="left"
                  width="w-max"
                  className="elevation-level-2!">
                  <span
                    className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full cursor-default",
                      subtypeConfig.color === "notice" && "background-notice-subtle content-action-notice",
                      subtypeConfig.color === "info" && "background-info-subtle content-action-info",
                    )}>
                    {subtypeConfig.icon}
                  </span>
                  <div
                    className="px-2.5 py-1.5 text-label-xs"
                    onMouseEnter={() => setHoveredSubtypeId(dropdownKey)}
                    onMouseLeave={() => setHoveredSubtypeId(null)}>
                    {subtypeLabel}
                  </div>
                </Dropdown>
              </div>
            )}
          </div>
        </Table.Cell>
        {!hideStatusColumn && (
          <Table.Cell>
            <div className="flex flex-col gap-0.5">
              {row.inStockCount > 0 && (
                <Badge
                  variant="soft"
                  color="positive"
                  text={`${row.inStockCount} ${t("table.inStockShort")}`}
                  icon={<Box size={12} color="currentColor" variant="Bold" />}
                />
              )}
              {row.assignedCount > 0 && (
                <Badge
                  variant="soft"
                  color="notice"
                  text={`${row.assignedCount} ${t("table.assignedShort")}`}
                  icon={<Profile2User size={12} color="currentColor" variant="Bold" />}
                />
              )}
            </div>
          </Table.Cell>
        )}
        <Table.Cell>
          <span className="text-label-sm content-action-neutral">—</span>
        </Table.Cell>
        <Table.Cell>
          <span className="text-label-sm content-action-neutral">—</span>
        </Table.Cell>
        <Table.Cell>
          <div className="flex flex-col gap-1">
            <span className="text-label-sm content-base-primary">{formatMoneyKzt(row.totalCost)}</span>
            <span className="text-label-xs content-action-neutral">
              {formatMoneyKzt(row.unitCost)} × {row.totalCount}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              isIconButton
              onClick={(e) => {
                e.stopPropagation();
                onBatchDetails?.(row);
              }}
              className="w-8! radius-xs! p-0!">
              <Eye size={16} color="currentColor" />
            </Button>
            {canManageInventory && (hasInStock || hasWriteOffable) && (
              <Dropdown
                open={openDropdownId === dropdownKey && !isBatchLoading}
                onClose={() => setOpenDropdownId(null)}
                direction={index >= rows.length - 2 ? "top" : "bottom"}>
                <Button
                  variant="secondary"
                  isIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(openDropdownId === dropdownKey ? null : dropdownKey);
                  }}
                  disabled={isBatchLoading}
                  className="w-8! radius-xs! p-0!">
                  <MoreIcon />
                </Button>
                {hasInStock && (
                  <DropdownItem
                    onClick={() => {
                      setOpenDropdownId(null);
                      onBatchAssign?.(row);
                    }}
                    disabled={isBatchAssigning}>
                    {isBatchAssigning ? t("loading") : t("actions.batchAssign")}
                  </DropdownItem>
                )}
                {hasWriteOffable && (
                  <DropdownItem
                    onClick={() => {
                      setOpenDropdownId(null);
                      onBatchWriteOff?.(row);
                    }}
                    disabled={isBatchWritingOff}>
                    {isBatchWritingOff ? t("loading") : t("actions.batchWriteOff")}
                  </DropdownItem>
                )}
              </Dropdown>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <>
      <Table.Table>
        <Table.Header>
          <tr>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "name" ? sortConfig.direction : undefined}
              onSort={() => onSort("name")}>
              {t("table.name")}
            </Table.HeadCell>
            <Table.HeadCell>{t("table.category")}</Table.HeadCell>
            {!hideStatusColumn && (
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "status" ? sortConfig.direction : undefined}
                onSort={() => onSort("status")}>
                {t("table.status")}
              </Table.HeadCell>
            )}
            <Table.HeadCell>{t("table.barcode")}</Table.HeadCell>
            <Table.HeadCell>{t("table.serialNumber")}</Table.HeadCell>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "price" ? sortConfig.direction : undefined}
              onSort={() => onSort("price")}>
              {t("table.cost")}
            </Table.HeadCell>
            <Table.HeadCell>{t("table.actions")}</Table.HeadCell>
          </tr>
        </Table.Header>
        <Table.Body>
          {rows.map((row, index) => {
            if (row.kind === "individual") {
              return renderIndividualRow(row.unit, index);
            }
            return renderAggregatedRow(row, index);
          })}
        </Table.Body>
      </Table.Table>

      {qrModalBarcode && (
        <ModalWrapper onClose={() => setQrModalBarcode(null)} width="w-96">
          <div className="flex flex-col items-center gap-6">
            <div className="w-full pb-3 border-b surface-base-stroke">
              <h3 className="text-body-bold-lg text-center content-base-primary">{t("qrModal.title")}</h3>
            </div>
            <div className="p-4 radius-md">
              <QRCodeCanvas ref={qrRef} value={qrUrl} size={240} level="M" bgColor="#ffffff" fgColor="#000000" />
            </div>

            <div className="flex flex-col gap-3 w-full">
              <Toast color="grey" text={t("qrModal.hint")} closable={false} autoClose={false} onClose={() => {}} />
              <div className="flex gap-3">
                <Button variant="secondary" size="md" className="flex-1" onClick={() => setQrModalBarcode(null)}>
                  {t("qrModal.close")}
                </Button>
                <Button variant="primary" size="md" className="flex-1" onClick={handleDownloadQR}>
                  {t("qrModal.download")}
                </Button>
              </div>
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
}
